import { createContext, useContext, type ReactNode } from "react";
import { CONTRACT_MESSAGES } from "@/lib/trackMessages";

/**
 * The contract is in a sent or paid royalty statement: as in the app's Edit
 * contract, its payees and splits can't change — those sections show the key
 * and their fields are disabled.
 */
const LockedContext = createContext(false);

export const LockedProvider = LockedContext.Provider;

export function useLocked() {
  return useContext(LockedContext);
}

/**
 * The rates alone can't change — the label's shares, by format and of expenses — as the
 * app's rates_locked: once the contract has journal entries. Also true whenever it's locked.
 */
const RatesLockedContext = createContext(false);

export const RatesLockedProvider = RatesLockedContext.Provider;

export function useRatesLocked() {
  return useContext(RatesLockedContext);
}

/** A section's rate fields, disabled while the rates are locked. */
export function RatesLockedFields({ children }: { children: ReactNode }) {
  const locked = useRatesLocked();
  return (
    <fieldset disabled={locked} className="contents">
      {children}
    </fieldset>
  );
}

/** A section's fields, all disabled while the contract is locked. */
export function LockedFields({ children }: { children: ReactNode }) {
  const locked = useLocked();
  return (
    <fieldset disabled={locked} className="contents">
      {children}
    </fieldset>
  );
}

/**
 * prototype-only: why editing is restricted — the contract has revenue, or is in a draft
 * or a sent royalty statement — and the tracks it already had, which can't be taken off.
 */
export type Blocking = {
  reason:
    | "revenue"
    | "expenses"
    | "draft-statement"
    | "sent-statement"
    | "paid-statement"
    | "journal-entries"
    | null;
  lockedTrackIds: string[];
};

/** As the app's can_delete false: the reasons that lock the contract (a draft statement and
 * journal entries alone don't). */
export const LOCKING_REASONS: Blocking["reason"][] = [
  "revenue",
  "expenses",
  "sent-statement",
  "paid-statement",
];
export const isLockingReason = (reason: string | null | undefined) =>
  !!reason && LOCKING_REASONS.includes(reason as Blocking["reason"]);

/** Why a locked contract's track can't be unlinked: its tooltip, wherever tracks are picked. */
export function lockedTrackReason(reason: Blocking["reason"]) {
  return reason === "revenue"
    ? "This track has revenue under this contract, so it can't be unlinked."
    : reason === "expenses"
      ? "This track has expenses under this contract, so it can't be unlinked."
      : "This track is in a royalty statement for this contract, so it can't be unlinked.";
}

const BlockingContext = createContext<Blocking>({ reason: null, lockedTrackIds: [] });

export const BlockingProvider = BlockingContext.Provider;

export function useBlocking() {
  return useContext(BlockingContext);
}

/** The locked sections' key icon: its tooltip. */
export const LOCKED_TOOLTIP = CONTRACT_MESSAGES.lockedTooltip;
